import React, { useState } from 'react';
import { Eye, EyeOff, ArrowRight, ArrowLeft } from 'lucide-react';
import FormField from '../../HrDashboard/utils/InputField';

const SignupForm = ({
  step,
  formData,
  errors,
  onInputChange,
  onNextStep,
  onBackStep,
  isLoading,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Step 1 Fields Definition
  const step1Fields = [
    {
      name: 'firstName',
      label: 'First Name',
      type: 'text',
    },
    {
      name: 'lastName',
      label: 'Last Name',
      type: 'text',
    },
    {
      name: 'email',
      label: 'Email Address',
      type: 'email',
    },
  ];

  // Step 2 Fields Definition
  const step2Fields = [
    {
      name: 'phone',
      label: 'Phone Number',
      type: 'tel',
    },
    {
      name: 'address',
      label: 'Street Address',
      type: 'text',
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
    },
    {
      name: 'state',
      label: 'State',
      type: 'text',
    },
    {
      name: 'zipCode',
      label: 'Zip Code',
      type: 'text',
    },
  ];

  return (
    <form className="space-y-5" >
      {step === 1 ? (
        // Step 1: Basic Information
        <>
          <div className="grid grid-cols-2 gap-4">
            {step1Fields.slice(0, 2).map((field) => (
              <div key={field.name}>
                <FormField
                  field={field}
                  value={formData[field.name]}
                  onChange={onInputChange}
                  isEditable={true}
                />
                {errors[field.name] && (
                  <p className="text-red-500 text-xs mt-1">{errors[field.name]}</p>
                )}
              </div>
            ))}
          </div>

          {step1Fields.slice(2).map((field) => (
            <div key={field.name}>
              <FormField
                field={field}
                value={formData[field.name]}
                onChange={onInputChange}
                isEditable={true}
              />
              {errors[field.name] && (
                <p className="text-red-500 text-xs mt-1">{errors[field.name]}</p>
              )}
            </div>
          ))}

          {/* Password Field with Show/Hide */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2" style={{ fontFamily: 'Montserrat' }}>
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={onInputChange}
                placeholder="Create a strong password"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 border-blue-300 bg-white font-medium"
                style={{ fontFamily: 'Montserrat' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-xs mt-1">{errors.password}</p>
            )}
            <p className="text-xs text-gray-500 mt-2">
              • At least 8 characters • Uppercase & lowercase • At least one number
            </p>
          </div>

          {/* Confirm Password Field */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2" style={{ fontFamily: 'Montserrat' }}>
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={onInputChange}
                placeholder="Confirm your password"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 border-blue-300 bg-white font-medium"
                style={{ fontFamily: 'Montserrat' }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
            )}
          </div>
        </>
      ) : (
        // Step 2: Additional Details
        <>
          {step2Fields.slice(0, 1).map((field) => (
            <div key={field.name}>
              <FormField
                field={field}
                value={formData[field.name]}
                onChange={onInputChange}
                isEditable={true}
              />
              {errors[field.name] && (
                <p className="text-red-500 text-xs mt-1">{errors[field.name]}</p>
              )}
            </div>
          ))}

          {/* Address Fields */}
          <div className="space-y-4">
            {step2Fields.slice(1, 3).map((field) => (
              <div key={field.name}>
                <FormField
                  field={field}
                  value={formData[field.name]}
                  onChange={onInputChange}
                  isEditable={true}
                />
                {errors[field.name] && (
                  <p className="text-red-500 text-xs mt-1">{errors[field.name]}</p>
                )}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {step2Fields.slice(3, 5).map((field) => (
              <div key={field.name}>
                <FormField
                  field={field}
                  value={formData[field.name]}
                  onChange={onInputChange}
                  isEditable={true}
                />
                {errors[field.name] && (
                  <p className="text-red-500 text-xs mt-1">{errors[field.name]}</p>
                )}
              </div>
            ))}
          </div>

          {/* Checkboxes */}
          <div className="space-y-3 pt-4 border-t border-gray-200">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="agreeTerms"
                checked={formData.agreeTerms}
                onChange={onInputChange}
                className="w-5 h-5 mt-0.5 rounded"
                style={{ accentColor: '#0095fa' }}
              />
              <span className="text-sm text-gray-700" style={{ fontFamily: 'Montserrat' }}>
                I agree to the{' '}
                <a href="#" className="text-blue-600 hover:underline font-semibold">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="#" className="text-blue-600 hover:underline font-semibold">
                  Privacy Policy
                </a>
              </span>
            </label>
            {errors.agreeTerms && (
              <p className="text-red-500 text-xs">{errors.agreeTerms}</p>
            )}

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="newsletter"
                checked={formData.newsletter}
                onChange={onInputChange}
                className="w-5 h-5 mt-0.5 rounded"
                style={{ accentColor: '#0095fa' }}
              />
              <span className="text-sm text-gray-700" style={{ fontFamily: 'Montserrat' }}>
                Send me updates about Samah Home Care
              </span>
            </label>
          </div>
        </>
      )}

      {/* Navigation Buttons */}
      <div className="flex gap-3 pt-4">
        {step === 2 && (
          <button
            type="button"
            onClick={onBackStep}
            disabled={isLoading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 transition-all duration-300 disabled:opacity-50"
          >
            <ArrowLeft size={18} />
            Back
          </button>
        )}

        <button
          type="button"
          onClick={onNextStep}
          disabled={isLoading}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold text-white transition-all duration-300 disabled:opacity-50"
          style={{
            backgroundColor: '#0095fa',
            boxShadow: '0 5px 15px rgba(0, 149, 250, 0.4)',
          }}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.target.style.backgroundColor = '#0f6caa';
              e.target.style.boxShadow = '0px 0px 20px rgba(0, 149, 250, 0.8)';
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#0095fa';
            e.target.style.boxShadow = '0 5px 15px rgba(0, 149, 250, 0.4)';
          }}
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Creating...
            </>
          ) : (
            <>
              {step === 1 ? 'Next' : 'Create Account'}
              <ArrowRight size={18} />
            </>
          )}
        </button>
      </div>

      {/* Sign In Link */}
      <p className="text-center text-gray-600 text-sm pt-2" style={{ fontFamily: 'Montserrat' }}>
        Already have an account?{' '}
        <a href="/login" className="text-blue-600 hover:text-blue-700 font-semibold transition-colors">
          Sign in
        </a>
      </p>
    </form>
  );
};

export default SignupForm;